import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Shield, Eye, X, CheckCircle, Clock,
  User, Package, CreditCard, Filter, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminFraud() {
  const [alerts, setAlerts] = useState([]);
  const [selfPurchaseBlocks, setSelfPurchaseBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alerts');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (activeTab === 'alerts') {
        const res = await axios.get(`${API_URL}/api/admin/fraud-alerts`, { headers });
        setAlerts(res.data.alerts || []);
      } else {
        const res = await axios.get(`${API_URL}/api/admin/self-purchase-blocked?page=${page}`, { headers });
        setSelfPurchaseBlocks(res.data.logs || []);
        setTotalPages(res.data.pages || 1);
      }
    } catch (error) {
      console.error('Failed to load fraud data:', error);
      toast.error('Failed to load fraud data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resolveAlert = async (alertId, status, notes = '') => {
    try {
      setResolving(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/fraud-alerts/${alertId}/resolve`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Alert resolved');
      setSelectedAlert(null);
      loadData();
    } catch (error) {
      toast.error('Failed to resolve alert');
    } finally {
      setResolving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAlertTypeBadge = (type) => {
    const badges = {
      'webhook_signature_invalid': { label: 'Webhook Rejection', color: 'bg-red-500/20 text-red-400', icon: Shield },
      'self_referral': { label: 'Self Referral', color: 'bg-orange-500/20 text-orange-400', icon: User },
      'duplicate_conversion': { label: 'Duplicate Conversion', color: 'bg-yellow-500/20 text-yellow-400', icon: CreditCard },
      'suspicious_download': { label: 'Suspicious Download', color: 'bg-[#00F0FF]/20 text-[#00F0FF]', icon: Package },
      'affiliate_anomaly': { label: 'Affiliate Anomaly', color: 'bg-blue-500/20 text-blue-400', icon: AlertTriangle },
    };
    return badges[type] || { label: type || 'Unknown', color: 'bg-neutral-500/20 text-neutral-400', icon: AlertTriangle };
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      'high': 'bg-red-500/20 text-red-400 border-red-500/30',
      'medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'low': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return badges[severity] || 'bg-neutral-500/20 text-neutral-400';
  };

  const filteredAlerts = filterType === 'all' 
    ? alerts 
    : alerts.filter(a => a.alert_type === filterType);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
              Fraud Dashboard
            </h1>
            <p className="text-neutral-400 mt-1">Monitor and respond to suspicious activity</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          <Button
            variant={activeTab === 'alerts' ? 'default' : 'ghost'}
            onClick={() => { setActiveTab('alerts'); setPage(1); }}
            className={activeTab === 'alerts' ? 'bg-white text-black' : ''}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Fraud Alerts ({alerts.length})
          </Button>
          <Button
            variant={activeTab === 'self-purchase' ? 'default' : 'ghost'}
            onClick={() => { setActiveTab('self-purchase'); setPage(1); }}
            className={activeTab === 'self-purchase' ? 'bg-white text-black' : ''}
          >
            <Shield className="w-4 h-4 mr-2" />
            Self-Purchase Blocks
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
          </div>
        ) : activeTab === 'alerts' ? (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={filterType === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterType('all')}
                className={filterType === 'all' ? 'bg-[#00F0FF] text-black' : 'border-white/20'}
              >
                All
              </Button>
              {['webhook_signature_invalid', 'self_referral', 'duplicate_conversion', 'suspicious_download'].map(type => (
                <Button
                  key={type}
                  size="sm"
                  variant={filterType === type ? 'default' : 'outline'}
                  onClick={() => setFilterType(type)}
                  className={filterType === type ? 'bg-[#00F0FF] text-black' : 'border-white/20'}
                >
                  {getAlertTypeBadge(type).label}
                </Button>
              ))}
            </div>

            {/* Alerts Table */}
            {filteredAlerts.length === 0 ? (
              <div className="text-center py-12 text-neutral-400">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No fraud alerts found</p>
              </div>
            ) : (
              <div className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-black/30">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Type</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Severity</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">User/Entity</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Details</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Time</th>
                        <th className="text-left p-4 text-sm font-medium text-neutral-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {filteredAlerts.map((alert) => {
                        const typeBadge = getAlertTypeBadge(alert.alert_type);
                        const TypeIcon = typeBadge.icon;
                        return (
                          <tr key={alert.alert_id} className="hover:bg-white/5">
                            <td className="p-4">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${typeBadge.color}`}>
                                <TypeIcon className="w-3 h-3" />
                                {typeBadge.label}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded border text-xs font-medium ${getSeverityBadge(alert.severity)}`}>
                                {alert.severity || 'medium'}
                              </span>
                            </td>
                            <td className="p-4 text-sm">
                              {alert.user_id || alert.affiliate_id || alert.creator_id || 'N/A'}
                            </td>
                            <td className="p-4 text-sm text-neutral-400 max-w-xs truncate">
                              {alert.reason || alert.notes || 'No details'}
                            </td>
                            <td className="p-4 text-sm text-neutral-400">
                              {formatDate(alert.created_at)}
                            </td>
                            <td className="p-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedAlert(alert)}
                                className="border-white/20"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Review
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Self-Purchase Blocks */}
            {selfPurchaseBlocks.length === 0 ? (
              <div className="text-center py-12 text-neutral-400">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No self-purchase blocks recorded</p>
              </div>
            ) : (
              <>
                <div className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-black/30">
                        <tr>
                          <th className="text-left p-4 text-sm font-medium text-neutral-400">User</th>
                          <th className="text-left p-4 text-sm font-medium text-neutral-400">Product</th>
                          <th className="text-left p-4 text-sm font-medium text-neutral-400">Blocked At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {selfPurchaseBlocks.map((block, idx) => (
                          <tr key={block._id || block.block_id || `spb-${block.user_id || 'u'}-${block.product_id || 'p'}-${idx}`} className="hover:bg-white/5">
                            <td className="p-4">
                              <div className="text-sm font-medium">{block.user_email || block.user_id}</div>
                              <div className="text-xs text-neutral-400">{block.user_name}</div>
                            </td>
                            <td className="p-4">
                              <div className="text-sm">{block.product_name || block.product_id}</div>
                            </td>
                            <td className="p-4 text-sm text-neutral-400">
                              {formatDate(block.blocked_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                      className="border-white/20"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-4 py-2 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage(p => p + 1)}
                      className="border-white/20"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Alert Detail Modal */}
        <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
          <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Fraud Alert Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedAlert && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-neutral-400">Alert ID</div>
                    <div className="font-mono">{selectedAlert.alert_id}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Type</div>
                    <div>{getAlertTypeBadge(selectedAlert.alert_type).label}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Severity</div>
                    <div className="capitalize">{selectedAlert.severity || 'medium'}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Time</div>
                    <div>{formatDate(selectedAlert.created_at)}</div>
                  </div>
                </div>

                {selectedAlert.user_id && (
                  <div>
                    <div className="text-neutral-400 text-sm mb-1">User ID</div>
                    <div className="font-mono text-sm bg-black/30 p-2 rounded">{selectedAlert.user_id}</div>
                  </div>
                )}

                {(selectedAlert.reason || selectedAlert.notes) && (
                  <div>
                    <div className="text-neutral-400 text-sm mb-1">Details</div>
                    <div className="text-sm bg-black/30 p-2 rounded">{selectedAlert.reason || selectedAlert.notes}</div>
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => resolveAlert(selectedAlert.alert_id, 'resolved', 'Marked as resolved')}
                    disabled={resolving}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Resolve
                  </Button>
                  <Button
                    onClick={() => resolveAlert(selectedAlert.alert_id, 'dismissed', 'False positive')}
                    disabled={resolving}
                    variant="outline"
                    className="flex-1 border-white/20"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Dismiss
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminFraud;
